import {
  Legend,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

export type NbsRadarSeries = {
  readonly id: string;
  readonly label: string;
  readonly color?: string;
};

export type NbsRadarAxisPoint = {
  readonly axis: string;
  readonly axisId: string;
  readonly [seriesId: string]: string | number;
};

export type NbsRadarTooltipMeta = {
  readonly axisId: string;
  readonly seriesId: string;
  readonly normalizedValue: number;
  readonly rawValue: number;
  readonly rawUnit: string;
  readonly normalizationMethod: string;
};

export type NbsRadarChartProps = {
  readonly data: readonly NbsRadarAxisPoint[];
  readonly series: readonly NbsRadarSeries[];
  readonly height?: number;
  readonly tooltipMeta?: readonly NbsRadarTooltipMeta[];
};

const DEFAULT_COLORS = ["#1f6f4a", "#c47b2c", "#2f5d8c"];

export function NbsRadarChart({
  data,
  series,
  height = 340,
  tooltipMeta = [],
}: NbsRadarChartProps) {
  return (
    <div data-testid="nbs-radar-chart" style={{ width: "100%", height }}>
      <ResponsiveContainer width="100%" height="100%">
        <RadarChart data={[...data]}>
          <PolarGrid stroke="rgba(0,0,0,0.12)" />
          <PolarAngleAxis dataKey="axis" tick={{ fontSize: 11 }} />
          <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 10 }} />
          <Tooltip
            formatter={(value: number, name: string, item) => {
              const payload = item?.payload as { axisId?: string } | undefined;
              const seriesId =
                series.find((s) => s.label === name)?.id ??
                series.find((s) => s.id === name)?.id ??
                name;
              const hit = tooltipMeta.find(
                (m) => m.seriesId === seriesId && m.axisId === payload?.axisId,
              );
              if (hit) {
                return [
                  `norm. ${hit.normalizedValue} · brut ${hit.rawValue} ${hit.rawUnit} · ${hit.normalizationMethod}`,
                  name,
                ];
              }
              return [`norm. ${value}`, name];
            }}
          />
          <Legend />
          {series.map((s, i) => (
            <Radar
              key={s.id}
              name={s.label}
              dataKey={s.id}
              stroke={s.color ?? DEFAULT_COLORS[i % DEFAULT_COLORS.length]}
              fill={s.color ?? DEFAULT_COLORS[i % DEFAULT_COLORS.length]}
              fillOpacity={0.2}
              isAnimationActive={false}
            />
          ))}
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
}
